import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { checkDailyLimit, getTimeUntilReset } from "../../../utils/dailyUsageManager";

const QuotaInfo = ({ onQuotaChange }) => {
  const [usageInfo, setUsageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeUntilReset, setTimeUntilReset] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsageInfo = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) {
          setError("Vui lòng đăng nhập để sử dụng tính năng");
          setLoading(false);
          if (onQuotaChange) {
            onQuotaChange();
          }
          return;
        }

        // Get daily limit info
        const limitInfo = await checkDailyLimit(userId);

        setUsageInfo(limitInfo);

        // Get time until reset
        const resetTime = getTimeUntilReset();
        setTimeUntilReset(resetTime);

        // Notify parent component about quota change
        if (onQuotaChange) {
          onQuotaChange();
        }
      } catch (error) {
        console.error("Error fetching usage info:", error);
        setError("Lỗi khi tải thông tin sử dụng");
        if (onQuotaChange) {
          onQuotaChange();
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUsageInfo();

    // Update countdown every minute
    const interval = setInterval(() => {
      const resetTime = getTimeUntilReset();
      setTimeUntilReset(resetTime);
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [onQuotaChange]);

  if (loading) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
          <span className="text-blue-700 text-sm">
            Đang tải thông tin sử dụng...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-red-600 mr-2">⚠️</span>
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!usageInfo) {
    return null;
  }

  const { success, remaining, limit, used, reason } = usageInfo;

  // Nếu đã hết lượt
  if (!success && reason === "limit_reached") {
    return (
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center">
            <span className="text-orange-600 mr-2">⏰</span>
            <span className="text-orange-700 text-sm font-medium">
              Đã hết lượt hôm nay ({used}/{limit} lượt)
            </span>
          </div>
          {timeUntilReset && (
            <span className="text-orange-600 text-xs">
              Reset sau: {timeUntilReset.formattedTime}
            </span>
          )}
        </div>
      </div>
    );
  }

  // Tính phần trăm đã sử dụng
  const usagePercentage = limit > 0 ? (used / limit) * 100 : 0;

  // Nếu sắp hết lượt (đã dùng >= 80%)
  if (usagePercentage >= 80) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center">
            <span className="text-yellow-600 mr-2">⚡</span>
            <span className="text-yellow-700 text-sm">
              Còn <span className="font-medium">{remaining}/{limit}</span> lượt hôm nay
            </span>
          </div>
          {timeUntilReset && (
            <span className="text-yellow-600 text-xs">
              Reset sau: {timeUntilReset.formattedTime}
            </span>
          )}
        </div>
      </div>
    );
  }

  // Hiển thị thông tin bình thường
  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center">
          <span className="text-green-600 mr-2">✅</span>
          <span className="text-green-700 text-sm">
            Miễn phí - Còn <span className="font-medium">{remaining}/{limit}</span> lượt hôm nay
          </span>
        </div>
        {timeUntilReset && (
          <span className="text-green-600 text-xs">
            Reset sau: {timeUntilReset.formattedTime}
          </span>
        )}
      </div>
    </div>
  );
};

export default QuotaInfo;
